import { normalizeAssetId, personalOwnerMatchesPerson } from './trackingModel';

function makeId(prefix) {
  const random = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${Date.now()}-${random}`;
}

function cleanPerson(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function personNamesMatch(left, right) {
  const a = cleanPerson(left);
  const b = cleanPerson(right);
  if (!a || !b) return false;
  if (a === b) return true;
  if (personalOwnerMatchesPerson(a, b) || personalOwnerMatchesPerson(b, a)) return true;

  const aParts = a.split(' ');
  const bParts = b.split(' ');
  if (aParts.length >= 2 && bParts.length >= 2) {
    const sameLast = aParts[aParts.length - 1] === bParts[bParts.length - 1];
    const aFirst = aParts[0];
    const bFirst = bParts[0];
    if (sameLast && (aFirst.startsWith(bFirst) || bFirst.startsWith(aFirst))) return true;
  }
  return false;
}

export function isLegacyBundleAsset(assetOrId) {
  const asset = assetOrId && typeof assetOrId === 'object' ? assetOrId : null;
  const id = normalizeAssetId(asset ? asset.id : assetOrId);
  const category = String(asset?.category || '').trim().toLowerCase();
  return id === 'GREG-GEAR'
    || /^LM-SET-\d+$/i.test(id)
    || category === 'line medic set'
    || category === 'personal set';
}

export function operationalAssets(state) {
  return (Array.isArray(state?.assets) ? state.assets : []).filter(asset => asset?.active !== false && !isLegacyBundleAsset(asset));
}

export function activeDeploymentsForPerson(state, person) {
  return (Array.isArray(state?.deployments) ? state.deployments : [])
    .filter(deployment => deployment?.status !== 'closed' && personNamesMatch(deployment?.person, person));
}

export function deploymentUsageItems(state, deploymentId) {
  const id = String(deploymentId || '').trim();
  if (!id) return [];
  const grouped = new Map();
  const events = Array.isArray(state?.events) ? [...state.events].reverse() : [];

  events.forEach(item => {
    if (item?.type !== 'supply_usage' || String(item?.deploymentId || '').trim() !== id) return;
    const assetId = normalizeAssetId(item?.assetId);
    const catalogId = String(item?.catalogId || '').trim();
    if (!assetId || !catalogId) return;
    const key = `${assetId}|${catalogId}`;
    const quantityDelta = Number(item?.quantityDelta ?? item?.quantity ?? 0);
    if (!Number.isFinite(quantityDelta) || quantityDelta === 0) return;
    const existing = grouped.get(key) || {
      deploymentId: id,
      assetId,
      assetName: String(item?.assetName || assetId).trim() || assetId,
      catalogId,
      component: String(item?.component || '').trim(),
      item: String(item?.item || catalogId).trim() || catalogId,
      quantity: 0,
    };
    existing.quantity += quantityDelta;
    if (item?.assetName) existing.assetName = String(item.assetName);
    if (item?.component) existing.component = String(item.component);
    if (item?.item) existing.item = String(item.item);
    grouped.set(key, existing);
  });

  return [...grouped.values()]
    .filter(item => item.quantity > 0)
    .sort((a, b) => a.assetName.localeCompare(b.assetName) || a.item.localeCompare(b.item));
}

export function usageForAsset(state, deploymentId, assetId) {
  const normalized = normalizeAssetId(assetId);
  return deploymentUsageItems(state, deploymentId).filter(item => item.assetId === normalized);
}

export function reportSupplyUsage(state, input) {
  const deploymentId = String(input?.deploymentId || '').trim();
  const assetId = normalizeAssetId(input?.assetId);
  const catalogId = String(input?.catalogId || '').trim();
  const item = String(input?.item || '').trim();
  const quantityDelta = Number(input?.quantityDelta);
  const actor = String(input?.actor || 'Member').trim() || 'Member';
  const source = String(input?.source || 'deployment').trim().toLowerCase() === 'return' ? 'return' : 'deployment';

  if (!deploymentId) throw new Error('Deployment ID is required.');
  if (!assetId) throw new Error('Gear asset is required.');
  if (!catalogId || !item) throw new Error('Inventory item is required.');
  if (!Number.isInteger(quantityDelta) || quantityDelta === 0 || Math.abs(quantityDelta) > 100) {
    throw new Error('Usage adjustment must be a whole number between -100 and 100.');
  }

  const deployment = (Array.isArray(state?.deployments) ? state.deployments : []).find(entry => entry.id === deploymentId);
  if (!deployment) throw new Error('Deployment was not found.');
  if (deployment.status === 'closed') throw new Error('This deployment is already closed.');
  const assignedIds = new Set((deployment.assetIds || []).map(normalizeAssetId));
  if (!assignedIds.has(assetId)) throw new Error('This gear is not assigned to the deployment.');

  const current = deploymentUsageItems(state, deploymentId)
    .find(entry => entry.assetId === assetId && entry.catalogId === catalogId)?.quantity || 0;
  if (current + quantityDelta < 0) throw new Error('Reported usage cannot go below zero.');

  const asset = (Array.isArray(state?.assets) ? state.assets : []).find(entry => normalizeAssetId(entry?.id) === assetId);
  const usageEvent = {
    id: makeId('EVT'),
    type: 'supply_usage',
    actor,
    timestamp: new Date().toISOString(),
    deploymentId,
    person: deployment.person,
    incident: deployment.incident,
    incidentOrderNumber: deployment.incidentOrderNumber || '',
    deploymentType: deployment.deploymentType || deployment.role || 'EMPF',
    assetId,
    assetName: String(input?.assetName || asset?.name || assetId).trim() || assetId,
    catalogId,
    component: String(input?.component || '').trim(),
    item,
    quantityDelta,
    source,
  };

  return {
    ...state,
    events: [usageEvent, ...(Array.isArray(state?.events) ? state.events : [])],
  };
}
